import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Calendar, Search, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NoticesPage = () => {
  const [notices, setNotices] = useState([]);
  const [filteredNotices, setFilteredNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notices`);
      setNotices(response.data);
      setFilteredNotices(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching notices:', err);
      toast.error('Failed to load notices');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = notices.filter(notice => 
      notice.title.toLowerCase().includes(term) || 
      notice.description.toLowerCase().includes(term)
    );
    setFilteredNotices(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Loading notices...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-[#4A90E2] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="h-10 w-10" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Notices & Announcements
            </h1>
          </div>
          <p className="text-xl text-gray-100">
            Stay updated with latest school announcements and events
          </p>
        </div>
      </div>

      {/* Search Section */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 py-6"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Notices List */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredNotices.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No notices found</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredNotices.map((notice) => (
                <Card 
                  key={notice.id} 
                  className={`shadow-md hover:shadow-lg transition-all duration-300 ${
                    notice.tag === 'Important'
                      ? 'border-l-4 border-l-red-500 bg-red-50/30'
                      : 'border-l-4 border-l-[#4A90E2]'
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(notice.date)}</span>
                      </div>
                      {notice.tag === 'Important' && (
                        <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                          IMPORTANT
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {notice.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      {notice.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NoticesPage;
